/**
 * Order Import React Query Hooks
 *
 * Provides React Query hooks for managing template fetching and file processing.
 * Hooks automatically include locale from Next.js router.
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import {
  commitOrderImportRows,
  fetchOrderImportTemplateUrl,
  processOrderImportFile,
  revalidateOrderImportRows,
} from './connector';
import type { OrderImportRevalidateLineInput, OrderImportSourceColumns } from './types';
import { SUPPORTED_ORDER_IMPORT_EXTENSIONS } from './types';

import { orderImportKeys } from '.';

const fileToBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const result = reader.result;

      if (typeof result !== 'string') {
        reject(new Error('Failed to read file content'));
        return;
      }

      const base64 = result.split(',')[1] ?? '';

      if (!base64) {
        reject(new Error('File payload is empty'));
        return;
      }

      resolve(base64);
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file content'));
    };

    reader.readAsDataURL(file);
  });

/**
 * React Query hook for fetching order import template URL.
 * Query only runs when tag is provided (enabled: !!tag).
 *
 * @param tag - Template identifier to fetch (query skipped if empty)
 * @returns UseQueryResult with OrderImportTemplateResponse or null
 *
 * @example
 * const { data, isLoading, error } = useOrderImportTemplate('orderImportTemplate');
 * if (data?.url) {
 *   // Template URL available for download
 * }
 */
const useOrderImportTemplate = (tag: string, options?: { enabled?: boolean }) => {
  const { locale } = useRouter();

  return useQuery({
    queryKey: orderImportKeys.template(tag, locale as string),
    queryFn: async () => {
      return await fetchOrderImportTemplateUrl(tag, locale as string);
    },
    enabled: (options?.enabled ?? true) && !!tag,
  });
};

/**
 * React Query mutation hook for processing uploaded files.
 * Automatically passes current locale to the mutation function.
 * Validates file extension before processing.
 *
 * @returns UseMutationResult<OrderImportProcessResponse, Error, File>
 *
 * @example
 * const { mutateAsync, isLoading, error } = useProcessOrderImportFile();
 * try {
 *   const result = await mutateAsync(selectedFile);
 *   console.log(`Processed ${result.validRows} valid rows`);
 * } catch (err) {
 *   console.error('Processing failed:', err);
 * }
 */
const useProcessOrderImportFile = () => {
  const { locale } = useRouter();
  const { t } = useTranslation('account');

  return useMutation({
    mutationFn: async (file: File) => {
      // Validate file extension early (before base64 conversion)
      const fileName = file.name.toLowerCase();
      const isSupported = Array.from(SUPPORTED_ORDER_IMPORT_EXTENSIONS).some(ext => fileName.endsWith(`.${ext}`));

      if (!isSupported) {
        throw new Error(String(t('orderImport.errors.file.unsupportedType')));
      }

      const contentBase64 = await fileToBase64(file);

      return await processOrderImportFile({
        fileName: file.name,
        mimeType: file.type,
        contentBase64,
        locale: locale as string,
      });
    },
  });
};

const useRevalidateOrderImportRows = () => {
  const { locale } = useRouter();

  return useMutation({
    mutationFn: async ({
      rows,
      fileName,
      sourceColumns,
    }: {
      rows: OrderImportRevalidateLineInput[];
      fileName?: string;
      sourceColumns?: OrderImportSourceColumns;
    }) => {
      return await revalidateOrderImportRows({
        rows,
        fileName,
        sourceColumns,
        locale: locale as string,
      });
    },
  });
};

const useCommitOrderImportRows = () => {
  const { locale } = useRouter();

  return useMutation({
    mutationFn: async ({ rows, fileName }: { rows: OrderImportRevalidateLineInput[]; fileName?: string }) => {
      return await commitOrderImportRows({
        rows,
        fileName,
        locale: locale as string,
      });
    },
  });
};

export { useOrderImportTemplate, useProcessOrderImportFile, useRevalidateOrderImportRows, useCommitOrderImportRows };
